import type { NavigateOptions, To } from 'react-router-dom';
import type { UploadWithProgressPayload } from '@components/upload-progress-bar/types.ts';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { LoginResponse } from '@routes/auth/___shared___/auth.api.ts';
import type {
    AvatarUploadResponse,
    PasswordRequest,
    UpdateUserDetailsParametersRequest,
    UserProfileChange,
} from '@routes/profile-bar/___pages___/my-profile/api/myProfile.api.ts';
import {
    changePassword,
    deleteUploadedPhoto,
    getUserRequestChanges,
    updateProfilePhotoWithUploadProgress,
    updateUserDetailsParams,
} from '@routes/profile-bar/___pages___/my-profile/api/myProfile.api.ts';
import { persistToken, readToken } from '@store/services/localStorage.service.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';
import { extractError } from '@utils/slice.ts';

type PendingRedirection = { redirect: (to: To, state?: any, opts?: NavigateOptions) => void };

interface MyProfileSlice {
    error: Record<string, any> | null;
    userProfileListChanges: PaginatedResponse<UserProfileChange, EmptyResultsDescription> | null;
    isSubmitForApprovalSend: boolean;
    isAboutToSubmit: boolean;
    isEditingProfile: boolean;
    isSubmitForApprovalModalOpened: boolean;
    isUnsubmittedChangesModalOpened: boolean;
    pendingRedirection: PendingRedirection | null;
    modifiedFields: Record<string, any> | null;
    isChangePhotoModalOpened: boolean;
    uploadedProfilePhotoURI: string | null;
    uploadedProfilePhotoContentId: string | null;
    isProfilePhotoUploaded: boolean;
    token: string | null;

    isGetUserRequestChangesPending: boolean;
    isUpdateUserDetailsParametersPending: boolean;
    isUpdateUserProfilePhotoPending: boolean;
    isDeleteProfilePhotoPending: boolean;
    isUpdatePasswordPending: boolean;
}

const initialState: MyProfileSlice = {
    error: null,
    userProfileListChanges: null,
    isSubmitForApprovalSend: false,
    isAboutToSubmit: true,
    isEditingProfile: false,
    isSubmitForApprovalModalOpened: false,
    isUnsubmittedChangesModalOpened: false,
    pendingRedirection: null,
    modifiedFields: null,
    isChangePhotoModalOpened: false,
    uploadedProfilePhotoURI: null,
    uploadedProfilePhotoContentId: null,
    isProfilePhotoUploaded: false,
    token: readToken(),

    isGetUserRequestChangesPending: false,
    isUpdateUserDetailsParametersPending: false,
    isUpdateUserProfilePhotoPending: false,
    isDeleteProfilePhotoPending: false,
    isUpdatePasswordPending: false,
};

export const getUserRequestChangesInfo = createAsyncThunk<
    PaginatedResponse<UserProfileChange, EmptyResultsDescription>,
    void,
    { rejectValue: any }
>('myProfile/getUserRequestChanges', (_, { rejectWithValue }) =>
    getUserRequestChanges()
        .then((res) => {
            return res;
        })
        .catch((err) => {
            return rejectWithValue(err);
        }),
);

export const updateUserDetailsParameters = createAsyncThunk<
    any,
    UpdateUserDetailsParametersRequest,
    { rejectValue: any }
>('myProfile/updateUserDetailsParams', (updateUserConsentPayload, { rejectWithValue }) =>
    updateUserDetailsParams(updateUserConsentPayload)
        .then((res) => {
            return res;
        })
        .catch((err) => {
            return rejectWithValue(err);
        }),
);

export const updateUserProfilePhotoWithUploadProgress = createAsyncThunk<
    AvatarUploadResponse,
    UploadWithProgressPayload,
    { rejectValue: any }
>('myProfile/updateProfilePhoto', ({ formData, onUploadProgress, signal }, { rejectWithValue }) =>
    updateProfilePhotoWithUploadProgress(formData, onUploadProgress, signal)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteProfilePhoto = createAsyncThunk<any, void, { rejectValue: any }>(
    'myProfile/deleteProfilePhoto',
    (_, { rejectWithValue }) =>
        deleteUploadedPhoto()
            .then((res) => {
                return res;
            })
            .catch((err) => {
                return rejectWithValue(err);
            }),
);

export const updatePassword = createAsyncThunk<LoginResponse, PasswordRequest, { rejectValue: any }>(
    'myProfile/updatePassword',
    (changePasswordRequest, { rejectWithValue }) =>
        changePassword(changePasswordRequest)
            .then((res) => {
                persistToken(res.accessToken);
                return res;
            })
            .catch((err) => {
                return rejectWithValue(err);
            }),
);

const myProfileSlice = createSlice({
    name: 'myProfile',
    initialState,
    reducers: {
        setIsSubmitForApprovalSend: (state, action: PayloadAction<boolean>) => {
            state.isSubmitForApprovalSend = action.payload;
        },
        setIsSubmitForApprovalModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isSubmitForApprovalModalOpened = action.payload;
        },
        setIsAboutToSubmit: (state, action: PayloadAction<boolean>) => {
            state.isAboutToSubmit = action.payload;
        },
        resetUserDetailsDataState: (state) => {
            state.isUnsubmittedChangesModalOpened = false;
            state.pendingRedirection = null;
            state.isEditingProfile = false;
        },
        setPendingRedirection: (state, action: PayloadAction<any>) => {
            state.pendingRedirection = action.payload;
        },
        setIsEditingProfile: (state, action: PayloadAction<boolean>) => {
            state.isEditingProfile = action.payload;
        },
        setIsUnsubmittedChangesModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isUnsubmittedChangesModalOpened = action.payload;
        },
        setModifiedFields: (state, action: PayloadAction<Record<string, any>>) => {
            state.modifiedFields = action.payload;
        },
        setIsChangePhotoModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isChangePhotoModalOpened = action.payload;
        },
        setUploadedProfilePhotoURI: (state, action: PayloadAction<string | null>) => {
            state.uploadedProfilePhotoURI = action.payload;
        },
        setUploadedProfilePhotoContentId: (state, action: PayloadAction<string | null>) => {
            state.uploadedProfilePhotoContentId = action.payload;
        },
        setIsProfilePhotoUploaded: (state, action: PayloadAction<boolean>) => {
            state.isProfilePhotoUploaded = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getUserRequestChangesInfo.pending, (state) => {
            state.error = null;
            state.isGetUserRequestChangesPending = true;
        });
        builder.addCase(getUserRequestChangesInfo.fulfilled, (state, action) => {
            state.userProfileListChanges = action.payload;
            state.isGetUserRequestChangesPending = false;
        });
        builder.addCase(getUserRequestChangesInfo.rejected, (state, action) => {
            state.error = extractError(action);
            state.isGetUserRequestChangesPending = false;
        });
        builder.addCase(updateUserDetailsParameters.pending, (state) => {
            state.error = null;
            state.isUpdateUserDetailsParametersPending = true;
        });
        builder.addCase(updateUserDetailsParameters.fulfilled, (state) => {
            state.isAboutToSubmit = false;
            state.isEditingProfile = false;
            state.isUpdateUserDetailsParametersPending = false;
        });
        builder.addCase(updateUserDetailsParameters.rejected, (state, action) => {
            state.isSubmitForApprovalModalOpened = false;
            state.error = extractError(action);
            state.isUpdateUserDetailsParametersPending = false;
        });
        builder.addCase(updateUserProfilePhotoWithUploadProgress.pending, (state) => {
            state.error = null;
            state.isUpdateUserProfilePhotoPending = true;
        });
        builder.addCase(updateUserProfilePhotoWithUploadProgress.fulfilled, (state, action) => {
            state.uploadedProfilePhotoURI = action.payload.avatarURI;
            state.uploadedProfilePhotoContentId = action.payload.contentId;
            state.isUpdateUserProfilePhotoPending = false;
        });
        builder.addCase(updateUserProfilePhotoWithUploadProgress.rejected, (state, action) => {
            state.error = extractError(action);
            state.uploadedProfilePhotoURI = null;
            state.uploadedProfilePhotoContentId = null;
            state.isUpdateUserProfilePhotoPending = false;
        });
        builder.addCase(deleteProfilePhoto.pending, (state) => {
            state.error = null;
            state.isDeleteProfilePhotoPending = true;
        });
        builder.addCase(deleteProfilePhoto.fulfilled, (state) => {
            state.isDeleteProfilePhotoPending = false;
        });
        builder.addCase(deleteProfilePhoto.rejected, (state, action) => {
            state.error = extractError(action);
            state.isDeleteProfilePhotoPending = false;
        });
        builder.addCase(updatePassword.pending, (state) => {
            state.error = null;
            state.isUpdatePasswordPending = true;
        });
        builder.addCase(updatePassword.fulfilled, (state, action) => {
            state.token = action.payload.accessToken;
            state.isUpdatePasswordPending = false;
        });
        builder.addCase(updatePassword.rejected, (state, action) => {
            state.error = extractError(action);
            state.isUpdatePasswordPending = false;
        });
    },
});

export const {
    setIsSubmitForApprovalSend,
    setIsSubmitForApprovalModalOpened,
    setIsAboutToSubmit,
    resetUserDetailsDataState,
    setPendingRedirection,
    setIsEditingProfile,
    setIsUnsubmittedChangesModalOpened,
    setModifiedFields,
    setIsChangePhotoModalOpened,
    setUploadedProfilePhotoURI,
    setUploadedProfilePhotoContentId,
    setIsProfilePhotoUploaded,
} = myProfileSlice.actions;

export default myProfileSlice.reducer;
